import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  Grid,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import Table, { buttonsStyle } from "../tables/table";
import { alertError, alertSuccess } from "../../../utils/logger";
import ConfirmDialog from "./ConfirmDialog";

export default function HeatsModal({ show, onHide, regatta, onHeatsChange }) {
  const [heats, setHeats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingHeat, setEditingHeat] = useState(null);
  const [heatToDelete, setHeatToDelete] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  useEffect(() => {
    if (regatta && regatta.heats) {
      setHeats(regatta.heats || []);
    }
  }, [regatta]);

  const handleAddHeat = () => {
    const newHeat = {
      // Don't include _id for new heats - let MongoDB generate it
      number: heats.length + 1,
      date: new Date().toISOString().slice(0, 10),
      startTime: "",
      endTime: "",
      name: `Heat ${heats.length + 1}`,
      notes: "",
      isNew: true,
      tempId: Date.now().toString() // Use tempId for local tracking only
    };
    setEditingHeat(newHeat);
    setShowEditDialog(true);
  };

  const handleEditHeat = (heat) => {
    setEditingHeat({
      ...heat,
      date: new Date(heat.date).toISOString().slice(0, 10)
    });
    setShowEditDialog(true);
  };

  const handleSaveHeat = () => {
    const updatedHeats = [...heats];
    
    if (editingHeat.isNew) {
      // For new heats, remove the temporary properties and let the backend handle _id
      const { isNew, tempId, ...heatData } = editingHeat;
      updatedHeats.push(heatData);
    } else {
      // For existing heats, find by _id and update
      const index = updatedHeats.findIndex(h => h._id === editingHeat._id);
      if (index >= 0) {
        updatedHeats[index] = { ...editingHeat };
      }
    }

    setHeats(updatedHeats);
    if (onHeatsChange) {
      onHeatsChange(updatedHeats);
    }
    
    setShowEditDialog(false);
    setEditingHeat(null);
    
    alertSuccess({
      title: "Saved!",
      customMessage: "Heat saved successfully.",
    });
  };

  const handleDeleteHeat = () => {
    const updatedHeats = heats.filter(h => {
      // For heats with _id (existing in database)
      if (heatToDelete._id && h._id) {
        return h._id !== heatToDelete._id;
      }
      // For new heats without _id, compare by number and name as fallback
      if (!heatToDelete._id && !h._id) {
        return !(h.number === heatToDelete.number && h.name === heatToDelete.name);
      }
      // If one has _id and the other doesn't, they're different
      return true;
    });
    
    setHeats(updatedHeats);
    
    if (onHeatsChange) {
      onHeatsChange(updatedHeats);
    }
    
    setOpenConfirmDialog(false);
    setHeatToDelete(null);
    
    alertSuccess({
      title: "Deleted!",
      customMessage: "Heat deleted successfully.",
    });
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const columns = [
    {
      dataField: "number",
      text: "Heat #",
      sort: true,
    },
    {
      dataField: "name",
      text: "Name",
      formatter: (value) => value || "-",
    },
    {
      dataField: "date",
      text: "Date",
      formatter: (value) => formatDate(value),
    },
    {
      dataField: "startTime",
      text: "Start Time",
      formatter: (value) => value || "-",
    },
    {
      dataField: "endTime",
      text: "End Time",
      formatter: (value) => value || "-",
    },
    {
      dataField: "notes",
      text: "Notes",
      formatter: (value) => value ? (value.length > 30 ? value.substring(0, 30) + "..." : value) : "-",
    },
    {
      text: "Actions",
      formatter: (cell, row) => (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            onClick={() => handleEditHeat(row)}
            style={buttonsStyle}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setHeatToDelete(row);
              setOpenConfirmDialog(true);
            }}
            style={buttonsStyle}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
      align: "center",
    }
  ];

  return (
    <>
      <Dialog
        open={show}
        onClose={onHide}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle disableTypography>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6">Heats ({heats.length})</Typography>
            <Button
              onClick={handleAddHeat}
              variant="contained"
              className="btn btn-primary"
              startIcon={<AddIcon />}
            >
              Add Heat
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Table
              keyField={(row) => row._id || row.tempId || row.number}
              data={heats}
              columns={columns}
              defaultSorted={[{ dataField: 'number', order: 'asc' }]}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onHide} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Add Heat Dialog */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingHeat?.isNew ? "Add New Heat" : "Edit Heat"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Heat Number"
                type="number"
                value={editingHeat?.number || ""}
                onChange={(e) => setEditingHeat({
                  ...editingHeat,
                  number: parseInt(e.target.value) || 1
                })}
                fullWidth
                margin="normal"
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Heat Name"
                value={editingHeat?.name || ""}
                onChange={(e) => setEditingHeat({
                  ...editingHeat,
                  name: e.target.value
                })}
                fullWidth
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Date"
                type="date"
                value={editingHeat?.date || ""}
                onChange={(e) => setEditingHeat({
                  ...editingHeat,
                  date: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Time"
                type="time"
                value={editingHeat?.startTime || ""}
                onChange={(e) => setEditingHeat({
                  ...editingHeat,
                  startTime: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Time"
                type="time"
                value={editingHeat?.endTime || ""}
                onChange={(e) => setEditingHeat({
                  ...editingHeat,
                  endTime: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={editingHeat?.notes || ""}
                onChange={(e) => setEditingHeat({
                  ...editingHeat,
                  notes: e.target.value
                })}
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveHeat} 
            variant="contained" 
            color="primary"
            disabled={!editingHeat?.number || !editingHeat?.date}
          >
            Save Heat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        title={'Are you sure you want to delete this heat?'}
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={handleDeleteHeat}
      />
    </>
  );
}